import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors duration-200"
          >
            <span className="font-heading text-xl font-semibold tracking-tight whitespace-nowrap">
              Vigorbuy<span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
            >
              Home
            </Link>
            <a
              href="#categories"
              className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
            >
              Categories
            </a>
            <Link
              to="/blog/vigorbuy-spreadsheet-ultimate-guide"
              className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
            >
              Shopping Guide
            </Link>
            <a
              href="#what-is"
              className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
            >
              About
            </a>
            <a
              href="#safety"
              className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
            >
              Safety
            </a>
            <a
              href="#faq"
              className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
            >
              FAQ
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-background-200/70 bg-background-50 max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <span className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-home-4-line"></i>
                </span>
                Home
              </Link>

              <button
                onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
                className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center gap-3">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-grid-line"></i>
                  </span>
                  Categories
                </span>
                <i className={`text-sm transition-transform duration-200 ${mobileCategoriesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
              </button>

              {mobileCategoriesOpen && (
                <div className="ml-5 pl-4 border-l-2 border-background-200/70 flex flex-col gap-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                        <i className={cat.icon}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}

              <Link
                to="/blog/vigorbuy-spreadsheet-ultimate-guide"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <span className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-book-open-line"></i>
                </span>
                Shopping Guide
              </Link>

              <Link
                to="/blog/vigorbuy-spreadsheet-safety-guide"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <span className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-shield-check-line"></i>
                </span>
                Safety Guide
              </Link>

              <a
                href="#faq"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <span className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-question-answer-line"></i>
                </span>
                FAQ
              </a>

              <div className="mx-3 my-2 border-t border-background-200/70"></div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="mx-3 flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-shopping-cart-line"></i>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}